import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  Button,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
} from '@mui/material';
import {
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import patientService, { Patient } from '../../services/patientService';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { GroupPatientChip } from '../common/GroupPatientChip';
import { PatientKrollStatusChips } from '../common/PatientKrollStatusChips';
import { PatientStatusChip } from '../common/PatientStatusChip';
import './PatientSearch.css';

// T087: Search type options
type SearchType = 'name' | 'phone' | 'address';

interface PharmacySearchResult {
  pharmacyId: string;
  pharmacyName: string;
  patients: Patient[];
  total: number;
  searchTime: number;
  expanded?: boolean;
}

/**
 * GP-308 — Optional props for embedding PatientSearch inside the global
 * patient-search dialog. When all are unset, the component behaves exactly
 * like before (used by the `/patients` deep-link page).
 */
export interface PatientSearchProps {
  /**
   * Called whenever the search input transitions between empty/non-empty.
   * The dialog uses this to hide the recent-searches header once the user
   * starts typing.
   */
  onQueryChange?: (hasQuery: boolean) => void;
  /**
   * Click-1 in the GP-308 two-click pattern. When provided, clicking a
   * patient row calls this instead of navigating, so the dialog can show
   * an inline quick view.
   */
  onPreviewPatient?: (patient: Patient, pharmacyId: string) => void;
  /**
   * Click-2 in the GP-308 two-click pattern. When provided, the "View 360°"
   * button calls this instead of `navigate(...)` directly, so the dialog
   * can record the visit to recent-searches before navigating.
   */
  onOpenPatient360?: (patient: Patient, pharmacyId: string) => void;
  /**
   * When set, that row is rendered with a "selected" highlight so the user
   * can see which patient the quick-view panel is showing.
   */
  selectedPatientId?: string | null;
  /**
   * Hide the search-form's own Paper wrapper and active-pharmacy chip
   * when this component is embedded inside the global dialog (which
   * already shows the pharmacy in its header). Reduces visual duplication.
   */
  embedded?: boolean;
}

export const PatientSearch: React.FC<PatientSearchProps> = ({
  onQueryChange,
  onPreviewPatient,
  onOpenPatient360,
  selectedPatientId,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const { activePharmacy } = usePharmacyStore();

  // T087: Search type state
  const [searchType, setSearchType] = useState<SearchType>('name');

  // Initialize state - no automatic population from query params
  const [lastName, setLastName] = useState('');
  // T087: Phone search state
  const [phoneSearch, setPhoneSearch] = useState('');
  // T087: Address search state
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<PharmacySearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [debouncedLastName] = useDebounce(lastName, 500);
  const [debouncedPhone] = useDebounce(phoneSearch, 500);
  const [debouncedStreet] = useDebounce(addressStreet, 500);
  const [debouncedCity] = useDebounce(addressCity, 500);
  const [debouncedPostalCode] = useDebounce(addressPostalCode, 500);

  const performSearch = useCallback(async () => {
    if (!activePharmacy) {
      setError('Please select a pharmacy first');
      return;
    }

    // Check search criteria - name search only for now
    if (!debouncedLastName || debouncedLastName.trim().length < 2) {
      setSearchResults([]);
      setTotalResults(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Name search - use MQTT search
      const raw = debouncedLastName.trim();
      let parsedLastName = raw;
      let parsedFirstName = '';
      if (raw.includes(',')) {
        const parts = raw.split(',');
        parsedLastName = parts[0].trim();
        parsedFirstName = parts.slice(1).join(',').trim();
      } else if (raw.includes(' ')) {
        const parts = raw.split(/\s+/);
        parsedLastName = parts[0];
        parsedFirstName = parts.slice(1).join(' ');
      }

      const result = await patientService.searchPatientsWithMQTT({
        lastName: parsedLastName,
        firstName: parsedFirstName,
        searchType: 'name',
        // Show all matches up to a generous cap. Safe to keep high because the
        // search endpoint no longer bulk-syncs every result (sync now happens when
        // a patient is opened), so a large result set costs only the Kroll SELECT.
        limit: 250,
        pharmacyId: activePharmacy.id,
      });

      // Ensure all patients have composite IDs
      const resultsWithCompositeIds = result.pharmacyResults.map(r => ({
        ...r,
        patients: r.patients.map(patient => ({
          ...patient,
          id: patient.id.includes('_')
            ? patient.id
            : `${activePharmacy.id}_${patient.id}`,
          patientId: patient.id.includes('_')
            ? patient.id
            : `${activePharmacy.id}_${patient.id}`,
        })),
        expanded: true,
      }));

      setSearchResults(resultsWithCompositeIds);
      setTotalResults(result.totalResults);
    } catch (err: any) {
      setError(err.message || 'Failed to search patients');
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedLastName, activePharmacy]);

  // Trigger search when name input changes
  useEffect(() => {
    const hasValidInput = !!(debouncedLastName && debouncedLastName.trim().length >= 2);

    if (hasValidInput) {
      performSearch();
    } else {
      // Clear results if input is cleared or too short
      setSearchResults([]);
      setTotalResults(0);
    }
  }, [debouncedLastName, performSearch]);

  // Tell the parent (global dialog) when the field transitions between empty
  // and non-empty so it can show/hide the recent-searches header.
  useEffect(() => {
    onQueryChange?.(lastName.trim().length > 0);
  }, [lastName, onQueryChange]);

  const togglePharmacyExpanded = (pharmacyId: string) => {
    setSearchResults(prev =>
      prev.map(result =>
        result.pharmacyId === pharmacyId
          ? { ...result, expanded: !result.expanded }
          : result
      )
    );
  };

  /**
   * Resolve the pharmacy ID for a patient row. PatientSearch is single-
   * pharmacy in practice (active pharmacy from the store) but composite IDs
   * also encode the pharmacyId for safety when the caller doesn't pass one.
   */
  const resolvePharmacyId = (patientId: string, pharmacyId?: string): string | undefined => {
    if (pharmacyId) return pharmacyId;
    if (patientId.includes('_')) return patientId.split('_')[0];
    return undefined;
  };

  /**
   * Row click — GP-308 click-1: preview if the dialog wired up a callback,
   * otherwise fall back to the original navigate behaviour for the
   * `/patients` deep-link page.
   */
  const handlePatientRowClick = (patient: Patient, pharmacyId?: string) => {
    const effectivePharmacyId = resolvePharmacyId(patient.id, pharmacyId);
    if (!effectivePharmacyId) return;
    if (onPreviewPatient) {
      onPreviewPatient(patient, effectivePharmacyId);
      return;
    }
    navigate(`/patients/${patient.id}`, {
      state: { pharmacyId: effectivePharmacyId, sourceSystem: 'mqtt' },
    });
  };

  /**
   * "View 360°" button — GP-308 click-2: always navigates. When the dialog
   * is hosting this component it intercepts to record the visit to recent
   * searches before navigating.
   */
  const handleOpen360 = (patient: Patient, pharmacyId?: string) => {
    const effectivePharmacyId = resolvePharmacyId(patient.id, pharmacyId);
    if (!effectivePharmacyId) return;
    if (onOpenPatient360) {
      onOpenPatient360(patient, effectivePharmacyId);
      return;
    }
    navigate(`/patients/${patient.id}`, {
      state: { pharmacyId: effectivePharmacyId, sourceSystem: 'mqtt' },
    });
  };

  // Format date for display - DD/MM/YYYY (Canadian/International standard)
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // T087: Handle search type change - clear inputs when switching
  const handleSearchTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: SearchType | null) => {
    if (newType) {
      setSearchType(newType);
      // Clear all search inputs when switching type
      setLastName('');
      setPhoneSearch('');
      setAddressStreet('');
      setAddressCity('');
      setAddressPostalCode('');
      setSearchResults([]);
      setTotalResults(0);
    }
  };

  return (
    <Box
      className={embedded ? undefined : 'patient-search-container'}
      // GP-956: when embedded in the global Find-Patients dialog, become a
      // full-height flex column so the results list below can scroll instead of
      // growing past the dialog. No effect on the standalone page usage.
      sx={embedded ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}
    >
      <Paper
        className={embedded ? undefined : 'patient-search-header'}
        elevation={embedded ? 0 : 2}
        sx={embedded ? { p: 0, background: 'transparent' } : undefined}
      >
        {/* T087: Search type toggle - DISABLED FOR NOW, will be a separate task
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={handleSearchTypeChange}
            aria-label="search type"
            size="small"
          >
            <ToggleButton value="name" aria-label="search by name">
              <PersonIcon sx={{ mr: 0.5 }} />
              Name
            </ToggleButton>
            <ToggleButton value="phone" aria-label="search by phone">
              <PhoneIcon sx={{ mr: 0.5 }} />
              Phone
            </ToggleButton>
            <ToggleButton value="address" aria-label="search by address">
              <AddressIcon sx={{ mr: 0.5 }} />
              Address
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        */}

        {/* Name search input - always shown */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Last Name (or 'Last, First')"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={!activePharmacy}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loading ? <CircularProgress size={20} /> : <PersonIcon />}
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* T087: Phone search - DISABLED FOR NOW, will be a separate task
        {searchType === 'phone' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              placeholder="Phone Number"
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              disabled={!activePharmacy}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? <CircularProgress size={20} /> : <PhoneIcon />}
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
              helperText="Enter at least 3 digits"
            />
          </Box>
        )}
        */}

        {/* T087: Address search - DISABLED FOR NOW, will be a separate task
        {searchType === 'address' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                placeholder="Street Address"
                value={addressStreet}
                onChange={(e) => setAddressStreet(e.target.value)}
                disabled={!activePharmacy}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {loading ? <CircularProgress size={20} /> : <AddressIcon />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                placeholder="City"
                value={addressCity}
                onChange={(e) => setAddressCity(e.target.value)}
                disabled={!activePharmacy}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                placeholder="Postal Code"
                value={addressPostalCode}
                onChange={(e) => setAddressPostalCode(e.target.value)}
                disabled={!activePharmacy}
              />
            </Grid>
          </Grid>
        )}
        */}

        {!embedded && (
          <Box className="patient-search-filters" sx={{ mt: 2 }}>
            {activePharmacy && (
              <Chip
                label={`Active: ${activePharmacy.name}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {totalResults > 0 && activePharmacy && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
          Found {totalResults} patient{totalResults !== 1 ? 's' : ''} in {activePharmacy.name}
        </Typography>
      )}

      <Box
        className="patient-search-results"
        // GP-956: in the embedded dialog this is the scroll region (the fixed
        // search header stays put, the results scroll). Standalone page: unchanged.
        sx={embedded ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}
      >
        {searchResults.map((pharmacyResult) => (
          <Paper
            key={pharmacyResult.pharmacyId}
            className="pharmacy-result-section"
            elevation={1}
            // GP-956: `.pharmacy-result-section` has overflow:hidden (for the rounded
            // corners), which makes its flexbox auto-min-size resolve to 0. As a flex
            // child of the now height-bounded `.patient-search-results` column it would
            // therefore SHRINK to the column height and clip the patient list internally
            // (overflow:hidden) instead of letting the column scroll. flexShrink:0 keeps
            // the section at its full content height so the column's overflow scrolls.
            sx={embedded ? { flexShrink: 0 } : undefined}
          >
            <Box
              className="pharmacy-result-header"
              onClick={() => togglePharmacyExpanded(pharmacyResult.pharmacyId)}
            >
              <Box className="pharmacy-info">
                <Typography variant="h6">{pharmacyResult.pharmacyName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {pharmacyResult.total} patient{pharmacyResult.total !== 1 ? 's' : ''} found • {pharmacyResult.searchTime}ms
                </Typography>
              </Box>
              <IconButton size="small">
                {pharmacyResult.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={pharmacyResult.expanded}>
              <List className="patient-list">
                {pharmacyResult.patients.map((patient) => (
                  <ListItem
                    key={patient.id}
                    onClick={() => handlePatientRowClick(patient, pharmacyResult.pharmacyId)}
                    className="patient-list-item"
                    sx={{
                      cursor: 'pointer',
                      ...(selectedPatientId === patient.id && {
                        bgcolor: 'action.selected',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                      }),
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Box component="span">{patient.lastName}, {patient.firstName}</Box>
                              {/* GP-1402: patient search now matches Kroll's alternate
                                  (maiden / former) surname too, so a row can surface under
                                  a name that isn't `lastName`. Naming it is what tells the
                                  user WHY this row matched what they typed. */}
                              {patient.alternateLastName &&
                                patient.alternateLastName.toUpperCase() !== (patient.lastName || '').toUpperCase() && (
                                  <Typography variant="caption" component="span" color="text.secondary">
                                    also {patient.alternateLastName}
                                  </Typography>
                                )}
                              {/* GP-1402: Kroll shows closed / deceased files and annotates
                                  them. PHOX used to hide them, so "not our patient" and
                                  "file closed" were indistinguishable. */}
                              <PatientKrollStatusChips patient={patient} />
                              {/* PRX-36: the PHOX-owned status, beside Kroll's
                                  own — a SIBLING chip, not a merge (see
                                  PatientStatusChip's docstring). Renders
                                  nothing for an active patient. */}
                              <PatientStatusChip status={patient.status} />
                            </Box>
                          }
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" component="span">
                                  MRN: {patient.mrn || 'N/A'}
                                </Typography>
                                {patient.dateOfBirth && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarIcon fontSize="small" sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                                    <Typography variant="caption" component="span" color="text.secondary">
                                      DOB: {formatDate(patient.dateOfBirth)}
                                    </Typography>
                                  </Box>
                                )}
                                {/* GP-1348: name + DOB alone can't tell four patients with the
                                    same name apart. Show WHICH nursing home the patient belongs to (the
                                    same labelled chip the queues use), never a generic "nursing
                                    home" tag — and nothing at all when they're not in one. */}
                                <GroupPatientChip group={patient.krollGroup} maxWidth={180} />
                              </Box>
                              {patient.primaryDiagnosis && (
                                <Chip
                                  label={patient.primaryDiagnosis}
                                  size="small"
                                />
                              )}
                            </Box>
                          }
                        />
                    <Box className="patient-actions">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen360(patient, pharmacyResult.pharmacyId);
                        }}
                      >
                        View 360°
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Paper>
        ))}
      </Box>

      {/* No results message */}
      {!loading && searchResults.length === 0 && lastName && lastName.trim().length >= 2 && (
        <Box className="no-results">
          <Typography variant="body1" color="textSecondary" align="center">
            No patients found matching "{lastName}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};